'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '@/hen-ui/utils';
import { SPACE_PB, SPACE_PX, SPACE_PY } from '@/hen-ui/utils/space-steps';
import { ChevronDownIcon } from 'lucide-react';

const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Root
    ref={ref}
    data-slot='accordion'
    className={cn('flex w-full flex-col gap-xxs', className)}
    {...props}
  />
));

Accordion.displayName = AccordionPrimitive.Root.displayName;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-slot='accordion-item'
    className={className}
    {...props}
  />
));

AccordionItem.displayName = AccordionPrimitive.Item.displayName;

// Optional trigger density. `default` keeps the original `p-sm`; `compact`
// tightens the vertical padding so a trigger can approach a denser reference.
export type AccordionTriggerDensity = 'default' | 'compact';

// Chevron size follows the HEN spacing scale; `xl` is the original default.
export type AccordionChevronSize = 'sm' | 'md' | 'lg' | 'xl';

// Where the title and chevron sit in the trigger row. `spread` (default)
// keeps today's layout, title on the left and chevron on the right. `end`
// and `start` group them together on one side of the row.
export type AccordionTriggerPosition = 'start' | 'end' | 'spread';

// Spacing steps authors can pick for Accordion padding / gap overrides.
export type AccordionSpaceStep = 'none' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

const TRIGGER_DENSITY_PADDING: Record<AccordionTriggerDensity, string> = {
  default: 'p-sm',
  compact: 'px-sm py-xs',
};

const DENSITY_AXIS_DEFAULTS: Record<
  AccordionTriggerDensity,
  { x: AccordionSpaceStep; y: AccordionSpaceStep }
> = {
  default: { x: 'sm', y: 'sm' },
  compact: { x: 'sm', y: 'xs' },
};

const CHEVRON_SIZE_CLASS: Record<AccordionChevronSize, string> = {
  sm: 'size-sm',
  md: 'size-md',
  lg: 'size-lg',
  xl: 'size-xl',
};

const TRIGGER_POSITION: Record<AccordionTriggerPosition, string> = {
  spread: 'justify-between',
  end: 'justify-end gap-min',
  start: 'justify-start gap-min',
};

const resolveTriggerPadding = (
  density: AccordionTriggerDensity,
  paddingX?: AccordionSpaceStep,
  paddingY?: AccordionSpaceStep,
): string => {
  if (paddingX == null && paddingY == null) {
    return TRIGGER_DENSITY_PADDING[density];
  }
  const axes = DENSITY_AXIS_DEFAULTS[density];
  return cn(SPACE_PX[paddingX ?? axes.x], SPACE_PY[paddingY ?? axes.y]);
};

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  density?: AccordionTriggerDensity;
  chevronSize?: AccordionChevronSize;
  /** Where the title and chevron sit in the row. Default `spread`. */
  triggerPosition?: AccordionTriggerPosition;
  /** Horizontal padding override. When set, replaces density on the X axis. */
  paddingX?: AccordionSpaceStep;
  /** Vertical padding override. When set, replaces density on the Y axis. */
  paddingY?: AccordionSpaceStep;
  // Semantic header element. `h3` (default) keeps the Radix heading semantics;
  // `div` drops them so a body-text title is not announced as a heading.
  headingTag?: 'h3' | 'div';
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(
  (
    {
      className,
      children,
      density = 'default',
      chevronSize = 'xl',
      triggerPosition = 'spread',
      paddingX,
      paddingY,
      headingTag: HeaderTag = 'h3',
      ...props
    },
    ref,
  ) => (
    <AccordionPrimitive.Header asChild>
      <HeaderTag className='flex'>
        <AccordionPrimitive.Trigger
          ref={ref}
          data-slot='accordion-trigger'
          className={cn(
            'group/accordion-trigger relative flex flex-1 items-center text-left',
            // Compare against the three known values. A direct lookup would
            // also match names inherited from Object (`constructor`,
            // `toString`), and those are truthy, so `??` would not fall back.
            triggerPosition === 'spread' ||
              triggerPosition === 'end' ||
              triggerPosition === 'start'
              ? TRIGGER_POSITION[triggerPosition]
              : TRIGGER_POSITION.spread,
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
            resolveTriggerPadding(density, paddingX, paddingY),
            // Keep a 44px minimum interactive target at every padding value.
            'min-h-11',
            'transition-all duration-normal ease-in-out',
            className,
          )}
          {...props}
        >
          {children}
          <ChevronDownIcon
            data-slot='accordion-trigger-icon'
            className={cn(
              'pointer-events-none shrink-0 text-neutral-600 flex items-center',
              CHEVRON_SIZE_CLASS[chevronSize],
              'transition-transform duration-fast ease-in-out',
              'group-aria-[expanded=true]/accordion-trigger:rotate-180',
            )}
          />
        </AccordionPrimitive.Trigger>
      </HeaderTag>
    </AccordionPrimitive.Header>
  ),
);

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> {
  /** Horizontal padding for the expanded body. Default `sm`. */
  paddingX?: AccordionSpaceStep;
  /** Bottom padding for the expanded body (top stays 0). Default `sm`. */
  paddingY?: AccordionSpaceStep;
}

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, paddingX, paddingY, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    data-slot='accordion-content'
    className={cn(
      'overflow-hidden',
      'data-[state=closed]:animate-accordion-up',
      'data-[state=open]:animate-accordion-down',
    )}
    {...props}
  >
    <div
      className={cn(
        'gap-sm',
        paddingX != null ? SPACE_PX[paddingX] : 'px-sm',
        paddingY != null ? SPACE_PB[paddingY] : 'pb-sm',
        className,
      )}
    >
      {children}
    </div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
